import React, { useState, useEffect } from 'react';
import { Search, Filter, FileText, Package, Crown, ChevronRight, Loader2, Download, TrendingUp, Star, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCheckoutSession } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: 'Credit Disputes & Credit Reporting', name: 'Credit Disputes' },
  { id: 'Business Credit & Business Setup', name: 'Business Credit' },
  { id: 'Court Motions & Legal Filings', name: 'Court Motions' },
  { id: 'Affidavits & Sworn Statements', name: 'Affidavits' },
  { id: 'Trust & Estate Planning', name: 'Trust & Estate' },
  { id: 'Legal Notices & Demand Letters', name: 'Legal Notices' },
  { id: 'Landlord-Tenant Disputes', name: 'Landlord Tenant' },
  { id: 'Lawsuits & Civil Complaints', name: 'Civil Lawsuit Templates & Filing Guide' },
];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [mostDownloaded, setMostDownloaded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('guides'); // guides | bundles
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodResp, bundleResp, topResp] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/bundles`),
          axios.get(`${API}/most-downloaded`),
        ]);
        const allProducts = prodResp.data.products;
        setProducts(allProducts.filter(p => p.product_type === 'guide' || !p.product_type));
        setBundles(bundleResp.data.bundles);
        setMostDownloaded(topResp.data.products);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSubcategory = selectedSubcategory === 'all' || product.subcategory === selectedSubcategory;
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSubcategory && matchesSearch;
  });

  const filteredBundles = bundles.filter(b => {
    const matchesCategory = selectedCategory === 'all' || b.category === selectedCategory || b.category === 'All Categories';
    const matchesSearch = b.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const subcategories = selectedCategory !== 'all'
    ? [...new Set(products.filter(p => p.category === selectedCategory).map(p => p.subcategory))]
    : [];

  const productsByCategory = categories.map(cat => ({
    ...cat,
    count: products.filter(p => p.category === cat.id).length,
  }));

  const handleQuickBuy = async (productId) => {
    setPurchasing(productId);
    try {
      const session = await createCheckoutSession(productId);
      if (session.url) window.location.href = session.url;
    } catch {
      alert('Unable to process. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  // Vault product
  const vault = bundles.find(b => b.product_type === 'vault');

  const BadgeTag = ({ badge }) => {
    if (!badge) return null;
    const config = {
      'Best Seller': { bg: 'bg-yellow-500', icon: <Star size={10} className="mr-1" /> },
      'Trending': { bg: 'bg-red-500', icon: <TrendingUp size={10} className="mr-1" /> },
      'Most Popular': { bg: 'bg-blue-500', icon: <Award size={10} className="mr-1" /> },
    };
    const c = config[badge] || { bg: 'bg-gray-500', icon: null };
    return (
      <span className={`${c.bg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center shadow-sm`}>
        {c.icon}{badge}
      </span>
    );
  };

  const DownloadCount = ({ count }) => {
    if (!count) return null;
    const display = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count;
    return (
      <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
        <Download size={10} /> {display}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-700 via-orange-600 to-orange-500 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" data-testid="shop-title">Documentation Library</h1>
          <p className="text-base md:text-lg text-white/90 mb-6">140+ Professional Legal Guides — Starting at $9.99</p>
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search guides..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 md:py-4 rounded-lg text-gray-900 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                data-testid="search-input"
              />
            </div>
          </div>
          {/* View Toggle */}
          <div className="flex justify-center gap-3 mt-6">
            <Button
              variant={viewMode === 'guides' ? 'default' : 'outline'}
              className={viewMode === 'guides' ? 'bg-white text-red-700 hover:bg-gray-100' : 'border-white text-white hover:bg-white/20'}
              onClick={() => setViewMode('guides')}
              data-testid="view-guides-btn"
            >
              <FileText size={16} className="mr-2" /> Individual Guides
            </Button>
            <Button
              variant={viewMode === 'bundles' ? 'default' : 'outline'}
              className={viewMode === 'bundles' ? 'bg-white text-red-700 hover:bg-gray-100' : 'border-white text-white hover:bg-white/20'}
              onClick={() => setViewMode('bundles')}
              data-testid="view-bundles-btn"
            >
              <Package size={16} className="mr-2" /> Bundles & Deals
            </Button>
          </div>
        </div>
      </section>

      {/* Vault Banner */}
      {vault && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 relative z-10">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl shadow-2xl p-5 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="vault-banner">
            <div className="flex items-center gap-4">
              <Crown className="w-10 h-10 text-yellow-400 shrink-0" />
              <div>
                <h2 className="text-lg md:text-xl font-bold">All Access Vault</h2>
                <p className="text-sm text-gray-300">All 140 guides — one price. Save over 85%</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-3xl font-bold text-yellow-400">${vault.price}</span>
              <Button
                className="bg-yellow-500 text-gray-900 hover:bg-yellow-400 font-bold px-6"
                onClick={() => window.location.href = `/bundle/${vault.id}`}
                disabled={purchasing === vault.id}
                data-testid="vault-buy-btn"
              >
                {purchasing === vault.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Get Full Access'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-5 sticky top-24" data-testid="category-sidebar">
              <div className="flex items-center gap-2 mb-4">
                <Filter size={18} className="text-red-700" />
                <h2 className="text-base font-bold text-gray-900">Categories</h2>
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => { setSelectedCategory('all'); setSelectedSubcategory('all'); }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${
                    selectedCategory === 'all'
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-md'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  data-testid="cat-all"
                >
                  All Guides ({products.length})
                </button>
                {productsByCategory.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { setSelectedCategory(cat.id); setSelectedSubcategory('all'); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-md'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    data-testid={`cat-${cat.name.replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{cat.name}</span>
                      <span className="text-xs opacity-75">({cat.count})</span>
                    </div>
                  </button>
                ))}
              </div>

              {subcategories.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Sub-Categories</h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => setSelectedSubcategory('all')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        selectedSubcategory === 'all' ? 'bg-orange-100 text-orange-700 font-semibold' : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >All</button>
                    {subcategories.map((subcat) => (
                      <button
                        key={subcat}
                        onClick={() => setSelectedSubcategory(subcat)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          selectedSubcategory === subcat ? 'bg-orange-100 text-orange-700 font-semibold' : 'hover:bg-gray-50 text-gray-600'
                        }`}
                      >{subcat}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Tiers */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 mb-2">Pricing</h3>
                <div className="space-y-1.5 text-sm text-gray-600">
                  <div className="flex justify-between"><span>Single Guide</span><span className="font-semibold text-gray-900">$9.99</span></div>
                  <div className="flex justify-between"><span>5-Guide Bundle</span><span className="font-semibold text-green-700">$39</span></div>
                  <div className="flex justify-between"><span>10-Guide Bundle</span><span className="font-semibold text-purple-700">$79</span></div>
                  <div className="flex justify-between"><span>Category Bundle</span><span className="font-semibold text-blue-700">$149</span></div>
                  <div className="flex justify-between"><span>All Access Vault</span><span className="font-semibold text-yellow-600">$700</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
              </div>
            )}

            {/* Bundles View */}
            {!loading && viewMode === 'bundles' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="bundles-heading">
                  Bundles & Deals
                  <span className="text-gray-500 text-lg ml-2">({filteredBundles.length})</span>
                </h2>

                {/* Category Bundles */}
                {filteredBundles.filter(b => b.product_type === 'bundle_category').length > 0 && (
                  <div className="mb-10">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Crown size={20} className="text-orange-600" /> Category Bundles — $149
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {filteredBundles.filter(b => b.product_type === 'bundle_category').map(bundle => (
                        <div key={bundle.id} className="bg-white rounded-xl shadow-md border-2 border-orange-200 overflow-hidden hover:shadow-xl transition-all" data-testid={`bundle-category-${bundle.id}`}>
                          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4 text-white">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-bold text-lg">{bundle.title}</h4>
                                <p className="text-sm text-white/80">{bundle.guide_count} guides included</p>
                              </div>
                              <div className="text-2xl font-bold">${bundle.price}</div>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{bundle.description}</p>
                            <div className="flex items-center justify-between">
                              <a href={`/bundle/${bundle.id}`} className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors">View Details</a>
                              <button
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                                onClick={() => window.location.href = `/bundle/${bundle.id}`}
                                disabled={purchasing === bundle.id}
                                data-testid={`buy-bundle-${bundle.id}`}
                              >
                                {purchasing === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                              </button>
                            </div>
                            <p className="text-[10px] text-gray-400 text-center mt-2">Also available in <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-500 transition-colors">Kindle bundle format on Amazon</a></p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 5-Guide Bundles */}
                {filteredBundles.filter(b => b.product_type === 'bundle_5').length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Package size={20} className="text-blue-600" /> 5-Guide Bundles — $39
                    </h3>
                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {filteredBundles.filter(b => b.product_type === 'bundle_5').map(bundle => (
                        <div key={bundle.id} className="bg-white rounded-xl shadow-md border border-blue-200 overflow-hidden hover:shadow-xl transition-all" data-testid={`bundle-5-${bundle.id}`}>
                          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-3 text-white flex items-center justify-between">
                            <div>
                              <h4 className="font-bold text-sm">{bundle.title}</h4>
                              <p className="text-xs text-white/80">{bundle.guide_count} guides</p>
                            </div>
                            <div className="text-xl font-bold">${bundle.price}</div>
                          </div>
                          <div className="p-4">
                            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{bundle.description}</p>
                            <div className="flex items-center justify-between">
                              <a href={`/bundle/${bundle.id}`} className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors">View Details</a>
                              <button
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                                onClick={() => window.location.href = `/bundle/${bundle.id}`}
                                disabled={purchasing === bundle.id}
                                data-testid={`buy-bundle-5-${bundle.id}`}
                              >
                                {purchasing === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                              </button>
                            </div>
                            <p className="text-[10px] text-gray-400 text-center mt-2">Also available in <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-500 transition-colors">Kindle bundle format on Amazon</a></p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 10-Guide Bundles */}
                {filteredBundles.filter(b => b.product_type === 'bundle_10').length > 0 && (
                  <div className="mt-10">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Package size={20} className="text-purple-600" /> 10-Guide Bundles — $79
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {filteredBundles.filter(b => b.product_type === 'bundle_10').map(bundle => (
                        <div key={bundle.id} className="bg-white rounded-xl shadow-md border border-purple-200 overflow-hidden hover:shadow-xl transition-all" data-testid={`bundle-10-${bundle.id}`}>
                          <div className="bg-gradient-to-r from-purple-500 to-violet-500 p-4 text-white">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-bold">{bundle.title}</h4>
                                <p className="text-sm text-white/80">{bundle.guide_count} guides included</p>
                              </div>
                              <div className="text-2xl font-bold">${bundle.price}</div>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{bundle.description}</p>
                            <div className="flex items-center justify-between">
                              <a href={`/bundle/${bundle.id}`} className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors">View Details</a>
                              <button
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                                onClick={() => window.location.href = `/bundle/${bundle.id}`}
                                disabled={purchasing === bundle.id}
                                data-testid={`buy-bundle-10-${bundle.id}`}
                              >
                                {purchasing === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                              </button>
                            </div>
                            <p className="text-[10px] text-gray-400 text-center mt-2">Also available in <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-500 transition-colors">Kindle bundle format on Amazon</a></p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {filteredBundles.length === 0 && (
                  <div className="text-center py-20">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No bundles found</h3>
                    <p className="text-gray-600">Try a different category or search term</p>
                  </div>
                )}
              </div>
            )}

            {/* Guides View */}
            {!loading && viewMode === 'guides' && (
              <div>
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-testid="guides-heading">
                    {selectedCategory === 'all' ? 'All Guides' : categories.find(c => c.id === selectedCategory)?.name}
                    <span className="text-gray-500 text-lg ml-2">({filteredProducts.length})</span>
                  </h2>
                </div>

                {filteredProducts.length > 0 ? (
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300"
                        data-testid={`product-card-${product.id}`}
                      >
                        <div className="h-40 bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center relative overflow-hidden">
                          <div className="absolute inset-0 bg-black opacity-10"></div>
                          <FileText className="w-16 h-16 text-white relative z-10" />
                          <div className="absolute top-3 right-3 bg-white text-red-700 font-bold text-sm px-3 py-1 rounded-full shadow-lg">
                            ${product.price}
                          </div>
                          {product.badge && (
                            <div className="absolute top-3 left-3 z-10">
                              <BadgeTag badge={product.badge} />
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                              {product.subcategory}
                            </span>
                            <DownloadCount count={product.download_count} />
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 mt-2 mb-1.5 line-clamp-2">{product.title}</h3>
                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                          <div className="flex gap-2">
                            <a
                              href={`/guide/${product.id}`}
                              className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors flex items-center"
                              data-testid={`view-details-${product.id}`}
                            >
                              View Details
                            </a>
                            <div className="flex-1"></div>
                            <button
                              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                              onClick={() => window.location.href = `/guide/${product.id}`}
                              disabled={purchasing === product.id}
                              data-testid={`quick-buy-${product.id}`}
                            >
                              {purchasing === product.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No guides found</h3>
                    <p className="text-gray-600">Try adjusting your filters or search term</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Free Guide Lead Magnet Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <a href="/free-guide" className="block" data-testid="lead-magnet-banner">
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-xl shadow-lg p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 hover:shadow-xl transition-shadow border border-gray-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center shrink-0">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-green-400 text-xs font-bold uppercase tracking-wider mb-1">Free Download</div>
                <h3 className="text-white font-bold text-lg">How, When, and Why to File a Lawsuit</h3>
                <p className="text-gray-400 text-sm">Get our free legal education guide — no purchase required</p>
              </div>
            </div>
            <div className="bg-green-500 text-white font-bold px-6 py-2.5 rounded-lg hover:bg-green-400 transition-colors text-sm shrink-0">
              Get Free Guide
            </div>
          </div>
        </a>
      </div>

      {/* Most Downloaded Guides */}
      {mostDownloaded.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="most-downloaded-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp size={24} className="text-orange-600" /> Most Downloaded Guides
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {mostDownloaded.map((p, i) => (
              <a
                key={p.id}
                href={`/guide/${p.id}`}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all border border-gray-100 hover:border-orange-200 block"
                data-testid={`most-downloaded-${i}`}
              >
                <div className="h-24 bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center relative">
                  <FileText className="w-10 h-10 text-white" />
                  {p.badge && (
                    <div className="absolute top-2 left-2">
                      <BadgeTag badge={p.badge} />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-white text-red-700 font-bold text-xs px-2 py-0.5 rounded-full shadow">
                    ${p.price}
                  </div>
                </div>
                <div className="p-3">
                  <h4 className="text-xs font-bold text-gray-900 line-clamp-2 mb-1">{p.title}</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-orange-600 font-medium">{p.subcategory}</span>
                    <DownloadCount count={p.download_count} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Shop;
